import React from 'react';
import PropTypes from 'prop-types';
import { cloneDeep, isEqual, isEmpty, find, map, indexOf } from 'lodash';
import uuid from 'uuid/v1';
import { Bert } from 'meteor/themeteorchef:bert';
import {
  Alert,
  Button,
  Box,
  TextField,
  Typography,
  Grid,
  Stepper,
  Step,
  StepLabel,
  Stack
} from '@mui/material';

import handleErrorInClient from '../../../../../modules/handle-error-in-client';
import methodPromise from '../../../../../modules/method-promise-wrapper';

import * as templateOps from '../../../../../api/AnnotationTemplates/annotation-template-operations';
import * as videoOps from '../../../../../api/Videos/video-operations';
import * as videoCalculate from '../../../../../api/Videos/videos-calculations';

import AnnotationsMapsEditTemplate from '../AnnotationsMapsEditTemplate';
import { ELEMENT_TYPES } from '../../../../../api/AnnotationTemplates/AnnotationTemplates';

import useTimeStore from '../../../ZustandProvider';

const CreateAnnotationsVideoPoint = props => {
  const {
    annotations,
    annotationTemplates,
    handleCloseEdit,
    handleNewAnnotationId,
    sizePlayer,
    timeVideoDuration,
    videoSelected,
    ...rest
  } = props;
  const steps = ['Anotación', 'Resumen'];
  const setNotesVideoEditor = useTimeStore(state => state.setNotesVideoEditor);
  const isNotesVideoEditor = useTimeStore(state => state.isNotesVideoEditor);
  const coordOnMap = useTimeStore(state => state.coordOnMap);

  const [selectedTemplate, setSelectedTemplate] = React.useState({});
  const [prevTemplate, setPrevTempalte] = React.useState(selectedTemplate);
  const [fieldsTemplate, setFieldsTemplate] = React.useState({});
  const [timeNewAnnotation, setTimeNewAnnotation] = React.useState(0);
  const [activeStep, setActiveStep] = React.useState(0);
  const [newId, setNewId] = React.useState('');
  const [manyNewAnnotation, setManyNewAnnotation] = React.useState([]);
  const [textNextButton, setTextNextButton] = React.useState('Finalizar');
  const [newCoords, setNewCoords] = React.useState(coordOnMap);
  const [autoTimePointVideo, setAutoTimePointVideo] = React.useState('none');

  const createAnnotation = () => {
    setNotesVideoEditor('add');
    setPrevTempalte(selectedTemplate);
    setManyNewAnnotation(prevAnnotation => [
      ...prevAnnotation,
      {
        newCoords: coordOnMap,
        newTime: timeNewAnnotation,
        templateProp: fieldsTemplate
      }
    ]);
    setSelectedTemplate({});
    setFieldsTemplate({});
    setNewCoords({});
    setTimeNewAnnotation(0);
    setAutoTimePointVideo('none');
  };

  const handleSelectedTemplateChange = templateProp => {
    const newSelectedTemplate = templateOps.getId(templateProp);
    const selectedAnnotationTemplate = find(annotationTemplates, template =>
      isEqual(templateOps.getId(template), newSelectedTemplate)
    );
    const newAnnotationTemplate = cloneDeep(selectedAnnotationTemplate);
    const newAnnotationTemplateElements = map(
      templateOps.getElements(newAnnotationTemplate),
      (element, index) => {
        const newElement = cloneDeep(element);
        if (!isEmpty(manyNewAnnotation)) {
          const lastTemplateCreate = manyNewAnnotation[manyNewAnnotation.length - 1];
          const prevElementsTemplate = lastTemplateCreate.templateProp.elements;
          const prevElement = prevElementsTemplate[index];
          newElement.value = prevElement.value;
          if (
            newElement.elementType === ELEMENT_TYPES.IMAPS &&
            prevElement.elementType === ELEMENT_TYPES.IMAPS
          ) {
            newElement.map = { mapId: prevElement.map.mapId };
          }
        } else {
          newElement.value = '';
        }
        return newElement;
      }
    );

    newAnnotationTemplate.elements = newAnnotationTemplateElements;
    setSelectedTemplate(newAnnotationTemplate);
    setFieldsTemplate(newAnnotationTemplate);
    setNotesVideoEditor('create');
  };

  const handleAutoTimePointVideo = () => {
    const time = videoCalculate.calculateTimeVideoPoint(annotations, manyNewAnnotation);
    setTimeNewAnnotation(time);
  };

  const AutomaticTimePointVideo = method => {
    setAutoTimePointVideo(method);
    if (method === 'auto') {
      handleAutoTimePointVideo();
    }
  };
  const handleTimePointVideo = event => {
    const { value } = event.target;
    const minutes = Number.parseInt(value.substr(0, 2), 10);
    const seconds = Number.parseInt(value.substr(3, 2), 10);
    if (value.substr(0, 2) === '00') {
      const time = seconds * 1e6;
      setTimeNewAnnotation(time);
    } else {
      const time = 60 * minutes + seconds;
      if (time > timeVideoDuration) {
        Bert.alert('Supera el tiempo máximo del video', 'danger');
      } else {
        setTimeNewAnnotation(time * 1e6);
      }
    }
  };

  const handleSubmit = async () => {
    const videoId = videoOps.getId(videoSelected);
    try {
      const annotationsToSave = manyNewAnnotation.map(element => {
        const newObject = {
          geoJSON: {
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [element.newCoords.lng, element.newCoords.lat]
            },
            properties: {
              video: videoId,
              videoTime: element.newTime,
              videoPosition: sizePlayer,
              template: element.templateProp
            }
          },
          origin: 'manual'
        };
        return newObject;
      });
      const newIdAnnotation = await methodPromise('annotations.insertMany', annotationsToSave);
      setNewId(newIdAnnotation[0]);
      Bert.alert('Annotation created', 'success');
      handleCloseEdit();
    } catch (error) {
      handleErrorInClient(error);
    }
  };

  const updateAnnotationFields = (elementItem, property, newValue) => {
    const newFieldsTemplate = cloneDeep(fieldsTemplate);
    const templateElementsArray = templateOps.getElements(fieldsTemplate);
    const newElementTemplate = map(templateElementsArray, element => {
      const newElement = element;
      if (
        isEqual(
          indexOf(templateElementsArray, element),
          indexOf(templateElementsArray, elementItem)
        )
      ) {
        newElement[property] = newValue;
      }

      return newElement;
    });
    newFieldsTemplate.elements = newElementTemplate;
    setFieldsTemplate(newFieldsTemplate);
  };

  const handleBack = () => {
    setTextNextButton('Finalizar');
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  };

  const handleNext = () => {
    if (activeStep === 0) {
      setTextNextButton('Crear Anotaciones');
      setActiveStep(prevActiveStep => prevActiveStep + 1);
    } else if (activeStep === steps.length - 1) {
      handleSubmit();
    } else {
      setActiveStep(prevActiveStep => prevActiveStep + 1);
    }
  };

  React.useEffect(() => {
    if (newId !== '') handleNewAnnotationId(newId);
  }, [newId]);

  React.useEffect(() => {
    if (isEmpty(selectedTemplate) && manyNewAnnotation.length > 0 && isNotesVideoEditor === 'add') {
      handleSelectedTemplateChange(prevTemplate);
    }
  }, [isNotesVideoEditor]);
  React.useEffect(() => {
    setNewCoords(coordOnMap);
  }, [coordOnMap]);

  const timeString = videoCalculate.secondsToString(timeVideoDuration);
  return (
    <>
      <Box display="flex" height="150px" width="100%" alignItems="center" mr={5}>
        <Grid container direction="row" justifyContent="space-around" spacing={3}>
          <Grid item xs={10} sx={{ justifyContent: 'center', display: 'flex' }}>
            <Box sx={{ width: '90%' }}>
              <Stepper activeStep={activeStep}>
                {steps.map(label => {
                  const labelProps = {};
                  return (
                    <Step key={label}>
                      <StepLabel {...labelProps}>{label}</StepLabel>
                    </Step>
                  );
                })}
              </Stepper>
            </Box>
          </Grid>
        </Grid>
      </Box>
      {activeStep === 0 && (
        <Box>
          {!isEmpty(selectedTemplate) && isEmpty(manyNewAnnotation) && (
            <Alert severity="info">Seleccione un punto en el mapa</Alert>
          )}
          {manyNewAnnotation.length > 0 && (
            <Alert severity="info">Seleccione más puntos para añadir más anotaciones</Alert>
          )}
          {!isEmpty(newCoords) && (
            <Alert severity="success">{`Coordenadas: ${newCoords.lat}, ${newCoords.lng}`}</Alert>
          )}
          {timeNewAnnotation !== 0 && (
            <Alert severity="success">{`El tiempo de la anotación es ${timeNewAnnotation *
              1e-6} segundos`}</Alert>
          )}
          <br />
          {!isEmpty(selectedTemplate) && !isEmpty(newCoords) && autoTimePointVideo === 'none' && (
            <Box display="flex" justifyContent="center" alignItems="center">
              <Stack direction="row">
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => AutomaticTimePointVideo('auto')}
                  size="small"
                >
                  Tiempo automático
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => AutomaticTimePointVideo('manual')}
                  size="small"
                >
                  Tiempo manual
                </Button>
              </Stack>
            </Box>
          )}
          {autoTimePointVideo === 'manual' && (
            <>
              <Typography variant="h5" gutterBottom>
                Selecciona un tiempo para la anotacion
              </Typography>
              <br />
              <TextField type="time" defaultValue="00:00" onChange={handleTimePointVideo} />
              <Alert severity="info">{`El tiempo del video es ${timeString} segundos`}</Alert>
            </>
          )}
          <br />
          <AnnotationsMapsEditTemplate
            annotationTemplates={annotationTemplates}
            handleSelectedTemplateChange={handleSelectedTemplateChange}
            selectedTemplate={selectedTemplate}
            updateAnnotationFields={updateAnnotationFields}
            isNotesComponent
            {...rest}
          />
        </Box>
      )}
      {activeStep === 1 && (
        <Box overflow="auto">
          <Alert severity="info">{`Se añadirán ${manyNewAnnotation.length} anotaciones`}</Alert>
          <br />
          {map(manyNewAnnotation, (element, index) => {
            return (
              <Box key={index}>
                <Alert severity="info" key={element.newTime}>
                  {`Anotación ${index + 1} -> Coordenadas: ${element.newCoords.lat} ${
                    element.newCoords.lng
                  } Tiempo: ${element.newTime * 1e-6} segundos`}
                </Alert>
                <br key={uuid()} />
              </Box>
            );
          })}
        </Box>
      )}
      <Box display="flex" height="100px" width="100%">
        <Stack direction="row" alignItems="center">
          <Button
            variant="outlined"
            color="primary"
            disabled={activeStep === 0}
            onClick={handleBack}
          >
            Atrás
          </Button>
          {activeStep === 0 && (
            <Button
              variant="outlined"
              color="success"
              disabled={isEmpty(selectedTemplate) || isEmpty(newCoords) || timeNewAnnotation === 0}
              onClick={() => {
                setNotesVideoEditor('add');
                createAnnotation();
              }}
            >
              Añadir
            </Button>
          )}
          <Button
            disabled={isEmpty(manyNewAnnotation)}
            variant="outlined"
            color={activeStep === steps.length - 1 ? 'success' : 'primary'}
            onClick={handleNext}
          >
            {textNextButton}
          </Button>
          <Button variant="outlined" color="error" onClick={handleCloseEdit}>
            Cancelar
          </Button>
        </Stack>
      </Box>
    </>
  );
};

CreateAnnotationsVideoPoint.defaultProps = {};

CreateAnnotationsVideoPoint.propTypes = {
  annotations: PropTypes.array.isRequired,
  annotationTemplates: PropTypes.array.isRequired,
  handleCloseEdit: PropTypes.func.isRequired,
  handleNewAnnotationId: PropTypes.func.isRequired,
  sizePlayer: PropTypes.object.isRequired,
  timeVideoDuration: PropTypes.number.isRequired,
  videoSelected: PropTypes.object.isRequired
};

export default CreateAnnotationsVideoPoint;
